import React from 'react';
import { useDeviceLayout } from "./DeviceLayoutProvider";

const BarChart = ({
    data = [],
    maxValue = 100,
    title = '',
    classes = ''
}) => {
    const { isLittle } = useDeviceLayout();
    // Calcola l'altezza massima per le barre (in percentuale)
    const maxHeight = 100;

    // Funzione per calcolare l'altezza di ogni barra
    const calculateBarHeight = (value) => {
        return (value / maxValue) * maxHeight;
    };

    // Funzione per calcolare la larghezza di ogni barra (quando isLittle è true)
    const calculateBarWidth = (value) => {
        return (value / maxValue) * 100; // Percentuale della larghezza disponibile
    };

    // Se isLittle è true, invertiamo gli assi
    if (isLittle) {
        return (
            <div className={`w-full h-full p-4 ${classes}`}>
                {/* Titolo del grafico */}
                {title && (
                    <h3 className="flex justify-self-center text-xl font-bold mb-6 text-center text-black dark:text-white uppercase">
                        {title}
                    </h3>
                )}

                <div className="relative w-full h-[90%]">
                    {/* Griglia di sfondo verticale per asse X invertito */}
                    <div className="absolute inset-0 flex justify-between">
                        {maxValue > 5 ? (
                            [0, 0.2, 0.4, 0.6, 0.8, 1].map((ratio, index) => (
                                <div
                                    key={index}
                                    className={`h-full ${ratio !== 0 && "border-r-4 border-dashed border-gray-400 dark:border-neutral-600"}`}
                                >
                                    {/* Etichetta valore asse X (ora verticale) */}
                                    <span className="absolute -top-2 transform -translate-x-1/2 -translate-y-full text-xs text-black dark:text-white font-semibold">
                                        {ratio !== 0 && Math.round(maxValue * ratio)}
                                    </span>
                                </div>
                            ))
                        ) : (
                            Array.from({ length: maxValue + 1 }, (_, i) => i).map((value, index) => (
                                <div
                                    key={index}
                                    className={`h-full ${value !== 0 && "border-r-4 border-dashed border-gray-400 dark:border-neutral-600"}`}
                                >
                                    {/* Etichetta valore asse X (ora verticale) */}
                                    <span className="absolute -top-2 transform -translate-x-1/2 -translate-y-full text-xs text-black dark:text-white font-semibold">
                                        {value !== 0 && value}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Contenitore per le barre orizzontali */}
                    <div className="absolute inset-0 py-2 flex flex-col justify-between">
                        {data.map((item, index) => {
                            const barWidth = calculateBarWidth(item.value);

                            return (
                                <div
                                    key={index}
                                    className="flex items-center w-full relative"
                                    style={{ height: `${100 / data.length}%` }}
                                >
                                    {/* Etichetta sull'asse Y (ora orizzontale) */}
                                    <div className="absolute transform -translate-x-full translate-y w-24 text-right">
                                        <p className="text-[40%] font-semibold text-black dark:text-white truncate">
                                            {item.label} ({item.value})
                                        </p>
                                    </div>

                                    {/* Contenitore per la barra orizzontale */}
                                    <div className="relative w-full h-full flex items-center">
                                        {/* Barra orizzontale */}
                                        <div
                                            className="h-2/3 rounded-sm bg-blue-600 transition-all duration-300 relative"
                                            style={{ width: `${barWidth}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    // Codice originale per quando isLittle è false
    return (
        <div className={`w-full h-full p-4 ${classes}`}>
            {/* Titolo del grafico */}
            {title && (
                <h3
                    className="flex justify-self-center text-xl font-bold mb-6 text-center text-black dark:text-white uppercase"
                >
                    {title}
                </h3>
            )}

            <div className="relative w-full h-[92%]">
                {/* Griglia di sfondo */}
                <div className="absolute inset-0 flex flex-col justify-between">
                    {
                        maxValue > 5 ? (
                            [1, 0.8, 0.6, 0.4, 0.2, 0].map((ratio, index) => (
                                <div
                                    key={index}
                                    className={`w-full ${ratio !== 0 && "border-t-4 border-dashed border-gray-400 dark:border-neutral-600"}`}
                                >
                                    {/* Etichetta valore asse Y */}
                                    <span
                                        className="absolute -left-2 transform -translate-x-full -translate-y-1/2 text-xs text-black dark:text-white font-semibold"
                                    >
                                        {ratio !== 0 && Math.round(maxValue * ratio)}
                                    </span>
                                </div>
                            ))
                        ) : (
                            Array.from({ length: maxValue + 1 }, (_, i) => maxValue - i).map((value, index) => (
                                <div
                                    key={index}
                                    className={`w-full ${value !== 0 && "border-t-4 border-dashed border-gray-400 dark:border-neutral-600"}`}
                                >
                                    {/* Etichetta valore asse Y */}
                                    <span
                                        className="absolute -left-2 transform -translate-x-full -translate-y-1/2 text-xs text-black dark:text-white font-semibold"
                                    >
                                        {value !== 0 && value}
                                    </span>
                                </div>
                            ))
                        )
                    }
                </div>

                {/* Contenitore per le barre */}
                <div className="absolute inset-0 px-4 flex items-end justify-between">
                    {data.map((item, index) => {
                        const barHeight = calculateBarHeight(item.value);

                        return (
                            <div
                                key={index}
                                className="flex flex-col items-center w-[10%] relative"
                                style={{ height: `${maxHeight}%` }}
                            >
                                {/* Contenitore principale */}
                                <div className="relative w-full flex-1 flex items-end">
                                    {/* Barra */}
                                    <div
                                        className="w-3/5 mx-auto rounded-lg bg-blue-600 transition-all duration-300 relative"
                                        style={{ height: `${barHeight}%` }}
                                    />
                                </div>

                                {/* Etichetta fluttuante - posizionata assolutamente in fondo al contenitore padre */}
                                <div className="absolute -bottom-9 left-1/2 transform -translate-x-1/2 text-center">
                                    <p className="text-xs text-nowrap font-semibold text-black dark:text-white">
                                        {item.label}
                                    </p>
                                    <p className="text-xs text-nowrap font-semibold text-black dark:text-white">
                                        ({item.value})
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default BarChart;